// Ported from the app; keep selection rules aligned with its source.
const artistKey = (item) => item.artistName?.trim().toLocaleLowerCase() ?? '';
const COMMUNITY_REASONS = new Set([
  'SIMILAR_LISTENERS_TRACK',
  'COMMUNITY_TOP_TRACK',
  'RECENTLY_RISING_TRACK',
  'EARLY_RESPONSE_TRACK',
  'POSTPOP_PICK_TRACK',
]);
function canUse(candidate, visible) {
  if (visible.some((item) => item.id === candidate.id || item.albumId === candidate.albumId))
    return false;
  if (visible.filter((item) => item.reason === candidate.reason).length >= 2) return false;
  if (
    candidate.reason === 'INTERESTED_ARTIST_TRACK' &&
    visible.some((item) => item.reason === candidate.reason)
  )
    return false;
  const artist = artistKey(candidate);
  return !artist || visible.filter((item) => artistKey(item) === artist).length < 2;
}
/**
 * Keeps the best initial tracks while preventing all three rows from carrying
 * the same reason. Each rotation replaces at most two slots from the wider
 * metadata-only pool; preview URLs are still requested for the visible window.
 */
export function selectRecommendedTrackWindow(pool, rotation, limit = 3) {
  const unique = pool.filter(
    (candidate, index) => pool.findIndex((item) => item.id === candidate.id) === index,
  );
  const visible = [];
  unique
    .filter((candidate) => COMMUNITY_REASONS.has(candidate.reason))
    .forEach((candidate) => {
      if (visible.length < Math.min(2, limit) && canUse(candidate, visible))
        visible.push(candidate);
    });
  unique.forEach((candidate) => {
    if (visible.length < limit && canUse(candidate, visible)) visible.push(candidate);
  });
  // Diversity is a ranking preference, not a reason to render fewer than the
  // promised requested rows. A small or temporarily homogeneous server pool
  // still needs to fill the three-slot home module.
  unique.forEach((candidate) => {
    if (visible.length < limit && !visible.some((item) => item.id === candidate.id))
      visible.push(candidate);
  });
  const queue = unique.filter((candidate) => !visible.some((item) => item.id === candidate.id));
  if (!visible.length || !queue.length) return visible;
  const rounds = Math.max(0, Math.floor(rotation)) % Math.max(1, unique.length * 2);
  for (let round = 0; round < rounds; round += 1) {
    const replacements = Math.min(2, visible.length, queue.length);
    for (let offset = 0; offset < replacements; offset += 1) {
      const slot = (round * 2 + offset) % visible.length;
      const withoutCurrent = visible.filter((_, index) => index !== slot);
      const replacementIndex = queue.findIndex((candidate) => canUse(candidate, withoutCurrent));
      if (replacementIndex < 0) continue;
      const [replacement] = queue.splice(replacementIndex, 1);
      const previous = visible[slot];
      visible[slot] = replacement;
      queue.push(previous);
    }
  }
  return visible;
}
/**
 * Keeps an already-visible window stable while the same rotation is active.
 * Background query refreshes can update metadata and fill missing slots, but
 * they must not reshuffle tracks merely because a sheet opened or fresh data
 * arrived. Deliberate rotation changes still select a new window.
 */
export function retainRecommendedTrackWindow(pool, rotation, previous, limit = 3) {
  const selected = selectRecommendedTrackWindow(pool, rotation, limit);
  if (!previous || previous.rotation !== rotation || previous.tracks.length === 0) return selected;
  const byId = new Map(pool.map((candidate) => [candidate.id, candidate]));
  const retained = previous.tracks
    .map((candidate) => byId.get(candidate.id) ?? candidate)
    .slice(0, limit);
  selected.forEach((candidate) => {
    if (retained.length < limit && !retained.some((item) => item.id === candidate.id))
      retained.push(candidate);
  });
  pool.forEach((candidate) => {
    if (retained.length < limit && !retained.some((item) => item.id === candidate.id))
      retained.push(candidate);
  });
  return retained.length > 0 ? retained : selected;
}
